import React, { useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useConnectionStatus } from '../../store/selectors';
import { useTradingLogic } from './useTradingLogic';
import { TradingStatusIndicator } from './TradingStatusIndicator';
import { TradingLockButton } from './TradingLockButton';
import { QuantitySelector } from './QuantitySelector';
import { OCOControls } from './OCOControls';
import { BuySellButtons } from './BuySellButtons';
import ScheduleSelector from './ScheduleSelector';
import { useScheduledOrderStore, resolveFireTime } from '../../store/scheduledOrderStore';
import FullSizeContractConfirmModal from '../FullSizeContractConfirmModal';
import { RestrainToggle } from '../Restrain';
import './ActionBarTrading.css';

interface ActionBarTradingProps {
  /** Optional symbol override for multi-chart/split mode */
  symbol?: string;
  /** Optional current price override for multi-chart/split mode */
  currentPrice?: number;
  /**
   * Bar interval in minutes, for candle-close scheduling. Defaults to the
   * app's 2-minute bar; pass the chart's actual timeframe where it's known.
   */
  intervalMinutes?: number;
}

export const ActionBarTrading: React.FC<ActionBarTradingProps> = ({
  symbol: propSymbol,
  currentPrice: propCurrentPrice,
  intervalMinutes: timeframeMinutes = 2,
}) => {
  // All hooks must be called unconditionally at the top
  const isMobile = useIsMobile();
  const connectionStatus = useConnectionStatus();
  const trading = useTradingLogic({
    symbol: propSymbol,
    currentPrice: propCurrentPrice,
  });

  // Fire a queued order when its moment arrives. Polls once a second rather
  // than a single long timeout so a tab throttled in the background still
  // catches up instead of firing minutes late.
  useEffect(() => {
    const id = setInterval(() => {
      const st = useScheduledOrderStore.getState();
      const p = st.pending;
      if (!p || Date.now() < p.fireAt) return;
      st.setPending(null);
      // Same handlers as an immediate click, so side, quantity and OCO TP/SL
      // are identical to what the panel shows.
      void (p.action === 'Buy' ? trading.handleBuy() : trading.handleSell());
    }, 1000);
    return () => clearInterval(id);
  }, [trading.handleBuy, trading.handleSell]);

  // On mobile, don't render - mobile uses the bottom trading bar
  if (isMobile) {
    return null;
  }

  // Don't render when WebSocket is disconnected
  if (connectionStatus !== 'connected') {
    return null;
  }

  // Defer to the scheduled time when one is set, otherwise fire immediately.
  const scheduleOr = (fn: () => void | Promise<void>, action: 'Buy' | 'Sell') => () => {
    const st = useScheduledOrderStore.getState();
    if (st.mode === 'now') {
      void fn();
      return;
    }
    const resolved = resolveFireTime(
      st.mode,
      Date.now(),
      timeframeMinutes,
      st.leadSeconds,
      st.atTime
    );
    // Unschedulable (a time already gone) — refuse rather than fire now.
    if (!resolved) return;
    st.setPending({ action, fireAt: resolved.fireAt, label: resolved.label });
  };

  return (
    <div className="action-bar-trading">
      {/* Status Section - only show when CLOSED or HEDGE is active */}
      {(!trading.isMarketOpen || trading.isHedgeCopyModeActive) && (
        <>
          <div className="action-bar-trading-section">
            <TradingStatusIndicator
              isMarketOpen={trading.isMarketOpen}
              isHedgeCopyModeActive={trading.isHedgeCopyModeActive}
            />
          </div>
          <div className="action-bar-trading-divider" />
        </>
      )}

      {/* Controls Section */}
      <div className="action-bar-trading-section">
        <TradingLockButton
          isDisciplineLocked={trading.isDisciplineLocked}
          lockMode={trading.disciplineLockMode}
          lockExpiresAt={trading.disciplineLockExpiresAt}
          lockDropdownOpen={trading.lockDropdownOpen}
          setLockDropdownOpen={trading.setLockDropdownOpen}
          startCoolOff={trading.startCoolOff}
          startCoolOffUntil={trading.startCoolOffUntil}
          setDoneForDay={trading.setDoneForDay}
          openUnlockModal={trading.openUnlockModal}
          openSettingsModal={trading.openSettingsModal}
          simpleUnlock={trading.simpleUnlock}
          simpleUnlockEnabled={trading.simpleUnlockEnabled}
          activeBroker={trading.activeBroker}
        />
        <RestrainToggle />
        <QuantitySelector
          selectedQuantity={trading.selectedQuantity}
          setSelectedQuantity={trading.setSelectedQuantity}
          quantities={trading.quantities}
        />
        <OCOControls
          useOCO={trading.useOCO}
          setUseOCO={trading.setUseOCO}
          ocoTPEnabled={trading.ocoTPEnabled}
          setOcoTPEnabled={trading.setOcoTPEnabled}
          ocoSLEnabled={trading.ocoSLEnabled}
          setOcoSLEnabled={trading.setOcoSLEnabled}
          ocoTPOffset={trading.ocoTPOffset}
          ocoSLOffset={trading.ocoSLOffset}
          handleTPChange={trading.handleTPChange}
          handleSLChange={trading.handleSLChange}
          isHedgeCopyModeActive={trading.isHedgeCopyModeActive}
        />
      </div>

      {/* Full-size confirm bypass toggle - only visible on full-size contracts */}
      {trading.isFullSize && (
        <button
          className={`trading-fullsize-toggle ${trading.fullSizeConfirmDisabled ? 'active' : ''}`}
          onClick={() => trading.setFullSizeConfirmDisabled(!trading.fullSizeConfirmDisabled)}
          title={trading.fullSizeConfirmDisabled ? 'Full-size confirm OFF (trading freely)' : 'Full-size confirm ON (will ask before each trade)'}
        >
          {trading.fullSizeConfirmDisabled ? 'FS:ON' : 'FS:OFF'}
        </button>
      )}

      <div className="action-bar-trading-divider" />

      {/* When the order fires. Part of the panel, not a separate control: it
          changes what BUY/SELL do, so it belongs next to them. */}
      <ScheduleSelector />

      <div className="action-bar-trading-divider" />

      {/* Action Buttons. Buy/sell are wrapped so a non-"Now" schedule queues
          instead of sending — same handler, deferred, so side/qty/TP/SL are
          identical to an immediate order. */}
      <BuySellButtons
        canTrade={trading.canTrade}
        isMarketOpen={trading.isMarketOpen}
        isCrypto={trading.isCrypto}
        handleBuy={scheduleOr(trading.handleBuy, 'Buy')}
        handleSell={scheduleOr(trading.handleSell, 'Sell')}
        handleFlatten={trading.handleFlatten}
        buyLock={trading.restrainBuyLock}
        sellLock={trading.restrainSellLock}
        buyLabel={trading.buyLabel}
        sellLabel={trading.sellLabel}
        buyTitle={trading.buyTitle}
        sellTitle={trading.sellTitle}
      />

      {/* Full-size contract confirmation modal */}
      <FullSizeContractConfirmModal
        isOpen={!!trading.fullSizeConfirm}
        symbol={trading.symbol}
        action={trading.fullSizeConfirm?.action || 'Buy'}
        quantity={trading.selectedQuantity}
        onConfirm={trading.handleFullSizeConfirm}
        onCancel={trading.handleFullSizeCancel}
      />
    </div>
  );
};

export default ActionBarTrading;
